// lazyWithReload — drop-in replacement for React.lazy() that recovers from
// the classic post-deploy Vite SPA failure mode:
//
//   "Failed to fetch dynamically imported module: …/assets/Foo-<hash>.js"
//
// Cause: the user has an already-loaded SPA holding references to chunk
// hashes that no longer exist on the server after a deploy. The next lazy
// import for an unvisited route 404s on the stale hash. Without this helper
// they get the React-Router error screen until they hard refresh.
//
// With this helper, the first chunk-load failure triggers a one-time
// `window.location.reload()` so the browser pulls the new manifest. A
// sessionStorage cookie prevents an infinite reload loop if the new build
// is genuinely broken (the second failure surfaces the real error).

import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const STALE_CHUNK_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk \d+ failed/i,
  /Loading CSS chunk \d+ failed/i,
]

const SESSION_KEY = 'vite-chunk-reload-attempted-at'

function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '')
  return STALE_CHUNK_PATTERNS.some(p => p.test(message))
}

/**
 * Global safety net for chunk loads lazyWithReload can't wrap: Vite emits
 * `vite:preloadError` for EVERY failed dynamic-import/preload (component-level
 * imports, chart chunks, …), not just lazy routes. Same reload-once guard.
 * Install once at bootstrap (main.tsx).
 */
export function installChunkReloadGuard(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const lastAttempt = Number(sessionStorage.getItem(SESSION_KEY) ?? 0)
    const now = Date.now()
    if (now - lastAttempt > 60_000) {
      event.preventDefault()               // suppress the unhandled rejection
      sessionStorage.setItem(SESSION_KEY, String(now))
      window.location.reload()
    }
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory()
    } catch (err) {
      if (isChunkLoadError(err)) {
        const lastAttempt = Number(sessionStorage.getItem(SESSION_KEY) ?? 0)
        const now = Date.now()
        // Reload at most once per 60s — long enough to swap manifests, short
        // enough that a genuinely broken build doesn't trap the user forever.
        if (now - lastAttempt > 60_000) {
          sessionStorage.setItem(SESSION_KEY, String(now))
          window.location.reload()
          // Returning a never-resolving promise keeps React Suspense quiet
          // while the page is unloading.
          return new Promise<{ default: T }>(() => {})
        }
      }
      throw err
    }
  })
}
